import { useCallback, useContext, useEffect, useMemo } from 'react';

import { AddFundsForm, PaymentMethodOption } from '~/components/modules/Forms/AddFundsForm';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { useSavedPaymentMethod } from '~/hooks/payments/useSavedPaymentMethod';
import { useTracking } from '~/hooks/tracking/useTracking';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';

import { useHandlePortfolioDestinationChange } from './PortfolioDestination';
import { useManagedPortfolioQuery } from '~/hooks/queries/portfolios/managed/useManagedPortfolioQuery';
import { useTradingPortfolioQuery } from '~/hooks/queries/portfolios/trading/useTradingPortfolioQuery';
import { PaymentMethodType } from '~/models/PaymentMethod';
import { PortfolioTypes } from '~/models/PortfolioType';

export const TransferFunds = ({ setStep }: ModalMultiStepProps) => {
    const { captureEvent } = useTracking();
    const { setDepositAmount, setDepositFrequency, setPaymentMethod, destinationPortfolioId, setDestinationPortfolio } =
        useContext(DepositContext);

    const handlePortfolioDestinationChange = useHandlePortfolioDestinationChange(setDestinationPortfolio);
    const { data: savedPaymentMethods, isSuccess } = useSavedPaymentMethod(destinationPortfolioId);
    const { data: managedPortfolio } = useManagedPortfolioQuery();
    const { data: tradingPortfolio } = useTradingPortfolioQuery();

    const paymentMethods: PaymentMethodOption[] = useMemo(() => {
        const allPaymentMethods = (savedPaymentMethods?.paymentMethods || []).map(({ id, card, ach }) => ({
            id,
            type: card ? PaymentMethodType.Card : PaymentMethodType.BankTransfer,
            name: `${card != null ? card.brand.toUpperCase() : ach?.name.toUpperCase()} ****${card != null ? card?.lastFour : ach?.lastFour}`,
            card,
            ach
        }));

        if (managedPortfolio?.id && managedPortfolio.cash?.amount && managedPortfolio.cash.amount > 0) {
            allPaymentMethods.push({
                id: managedPortfolio.id,
                type: PaymentMethodType.VinovestFunds,
                name: `Managed Investing`,
                card: null,
                ach: null
            });
        }

        if (tradingPortfolio?.id && tradingPortfolio.cash?.amount && tradingPortfolio.cash.amount > 0) {
            allPaymentMethods.push({
                id: tradingPortfolio.id,
                type: PaymentMethodType.VinovestFunds,
                name: `Available Balance`,
                card: null,
                ach: null
            });
        }

        return allPaymentMethods;
    }, [savedPaymentMethods, managedPortfolio, tradingPortfolio]);

    const onDepositInfoEntered = useCallback(
        async (data: any) => {
            captureEvent(AnalyticsEventNames.DepositAmountAdded, {
                amount: Number(data?.amount),
                frequency: data?.frequency
            });
            setDepositAmount(Math.abs(Number(data?.amount)));
            setDepositFrequency(data?.frequency);
            await handlePortfolioDestinationChange(data?.destination);

            const selectedMethod = paymentMethods.find((value: { id: string }) => value?.id === data?.source);

            setPaymentMethod(selectedMethod);
            captureEvent(AnalyticsEventNames.ClickExistingPayment, undefined);
            setStep(1);
        },
        [destinationPortfolioId, savedPaymentMethods]
    );

    const onPaymentChanged = (updatedPayment: string) => {
        if (updatedPayment === managedPortfolio?.id) {
            setDestinationPortfolio(PortfolioTypes.trading);
        }
        if (updatedPayment === tradingPortfolio?.id) {
            setDestinationPortfolio(PortfolioTypes.managed);
        }
        if (updatedPayment === 'new') {
            setStep(3);
        }
    };

    useEffect(() => {
        if (savedPaymentMethods?.paymentMethods.length === 0) {
            setStep(3);
        } else {
            setStep(0);
        }
    }, [isSuccess, savedPaymentMethods?.paymentMethods, setStep, destinationPortfolioId]);

    return (
        <AddFundsForm
            paymentMethods={paymentMethods}
            onPaymentChanged={onPaymentChanged}
            onDepositInfoEntered={onDepositInfoEntered}
            loadedPaymentOptions={isSuccess}
        />
    );
};
